/**
 * useLocationSchedules — fetch aircraft_schedules for a location on a given date.
 *
 * Returns AircraftSchedule[] with aircraftId, suitable for the Today Board.
 */

import { useCallback, useMemo } from 'react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchLocationSchedulesForDate } from '@/lib/api/aircraft-schedules'
import type { AircraftSchedule } from '@/types/aircraft-schedules'

export type { AircraftSchedule }

export const locationScheduleKeys = {
  all: ['location-schedules'] as const,
  forDate: (locationId: string, dateKey: string) =>
    ['location-schedules', locationId, dateKey] as const,
}

/** Local-timezone day boundaries + a stable YYYY-MM-DD cache key for a date. */
export function dayBounds(date: Date) {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  const end = new Date(date)
  end.setHours(23, 59, 59, 999)
  const dateKey = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`
  return { dateStart: start.toISOString(), dateEnd: end.toISOString(), dateKey }
}

// Visited days stay in cache for 30 min so re-selecting a day is instant.
const SCHEDULE_GC_TIME = 30 * 60_000

export function useLocationSchedules(locationId: string | null, selectedDate: Date) {
  const { dateStart, dateEnd, dateKey } = useMemo(() => dayBounds(selectedDate), [selectedDate])

  return useQuery({
    queryKey: locationScheduleKeys.forDate(locationId ?? '', dateKey),
    queryFn: () => fetchLocationSchedulesForDate(locationId!, dateStart, dateEnd),
    enabled: !!locationId,
    staleTime: 60_000,
    gcTime: SCHEDULE_GC_TIME,
    // Keep the previous day's rows on screen while the new day loads — no flash to empty.
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
  })
}

/**
 * Returns a function to warm the cache for arbitrary dates (today ±window, next few days).
 * Uses the exact same query key + fetcher as useLocationSchedules so prefetched days are
 * served instantly on selection.
 */
export function usePrefetchLocationSchedules(locationId: string | null) {
  const queryClient = useQueryClient()

  return useCallback(
    (dates: Date[]) => {
      if (!locationId) return
      for (const date of dates) {
        const { dateStart, dateEnd, dateKey } = dayBounds(date)
        queryClient.prefetchQuery({
          queryKey: locationScheduleKeys.forDate(locationId, dateKey),
          queryFn: () => fetchLocationSchedulesForDate(locationId, dateStart, dateEnd),
          staleTime: 60_000,
          gcTime: SCHEDULE_GC_TIME,
        })
      }
    },
    [locationId, queryClient],
  )
}
